import { useState } from "react";
import { PageHero } from "./Services";
import { api, errMsg } from "../../lib/api";
import { COMPANY } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Mail, MessageCircle, MapPin, Loader2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Message sent. We'll respond within one business day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
    setLoading(false);
  };

  return (
    <div>
      <PageHero overline="Contact" title="Get in touch." sub="Questions, custom operations, or a mission to plan — our team responds fast." />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-5 gap-10">
        <form onSubmit={submit} className="lg:col-span-3 rounded-xl border bg-card p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Your name" required />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="your@email.com" required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input data-testid="contact-subject-input" value={form.subject} onChange={set("subject")} placeholder="What's this about?" />
          </div>
          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea data-testid="contact-message-input" rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your project" required />
          </div>
          <Button data-testid="contact-submit-button" type="submit" disabled={loading} className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold px-8">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Message"}
          </Button>
        </form>
        <div className="lg:col-span-2 space-y-5">
          {[
            { icon: Mail, title: "Email Us", text: COMPANY.email, href: `mailto:${COMPANY.email}` },
            { icon: MessageCircle, title: "Live Chat", text: "Available 24/7 for urgent inquiries — use the assistant in the corner." },
            { icon: MapPin, title: "Operations", text: "San Diego & Oceanside HQ · Long distance to LA & Vegas" },
          ].map((c) => (
            <div key={c.title} className="rounded-xl border bg-card p-6 card-lift flex gap-4">
              <div className="w-11 h-11 rounded-xl bg-[var(--cc-accent)]/10 flex items-center justify-center shrink-0">
                <c.icon className="w-5 h-5 text-[var(--cc-accent)]" />
              </div>
              <div>
                <h3 className="font-heading font-semibold">{c.title}</h3>
                {c.href ? <a href={c.href} className="text-sm text-[var(--cc-accent)] mt-1 block">{c.text}</a>
                  : <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{c.text}</p>}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
